import { appState } from '../core/stateManager.js';
import { FileParser } from '../services/fileParser.js';
import { searchService } from '../services/searchService.js';
import { TemplateService } from '../services/templateService.js';
import { aiService } from '../services/aiService.js';
import { ragService } from '../services/ragService.js';
import { getAdminState } from './admin.js';
import { updateProjectDropdown, newProject, showDashboard } from './navigation.js';
import { qStep } from './quickdoc.js';

window.deleteProject = function(e, id) {
    e.stopPropagation();
    if(confirm("삭제하시겠습니까?")) {
        appState.deleteProject(id);
        updateProjectDropdown();
        if (appState.getState().currentProjectId === id) {
            appState.newProject();
        } else {
            renderDashboard();
        }
    }
};

// Hybrid Document Viewer State
let isRHWPInitialized = false;
let hwpDocInstance = null;
let currentHwpPage = 0;
let hwpPageCount = 0;
let activePreviewFile = null;
let activePreviewType = ''; // 'hwp', 'pdf', 'text', 'office'
let activePreviewBlobUrl = '';
let activePreviewTextContent = '';
let activeSourcingAttachmentExtractionKey = '';

// Register global measureTextWidth callback if not present
if (!globalThis.measureTextWidth) {
    let ctx = null;
    globalThis.measureTextWidth = (font, text) => {
        if (!ctx) ctx = document.createElement('canvas').getContext('2d');
        ctx.font = font;
        return ctx.measureText(text).width;
    };
}

async function initRHWP() {
    if (isRHWPInitialized) return;
    try {
        const { default: init } = await import('@rhwp/core');
        await init({ module_or_path: '/rhwp_bg.wasm' });
        isRHWPInitialized = true;
    } catch (err) {
        console.error("Failed to initialize @rhwp/core WASM:", err);
        throw new Error("한글 뷰어 엔진 초기화 실패: " + err.message);
    }
}

async function renderHwpPage(pageNum) {
    if (!hwpDocInstance) return;
    const contentArea = document.getElementById('p-hwp-viewer-content');
    const pageInfo = document.getElementById('hwp-page-info');
    if (!contentArea || !pageInfo) return;

    try {
        contentArea.innerHTML = '<div class="text-slate-400 text-xs py-8 text-center">페이지 렌더링 중...</div>';
        
        const svgString = hwpDocInstance.renderPageSvg(pageNum);
        if (svgString) {
            contentArea.innerHTML = svgString;
            
            const svgEl = contentArea.querySelector('svg');
            if (svgEl) {
                svgEl.style.width = '100%';
                svgEl.style.height = 'auto';
                svgEl.style.maxWidth = '100%';
                svgEl.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
                svgEl.style.background = '#ffffff';
                svgEl.style.borderRadius = '4px';
            }
        } else {
            contentArea.innerHTML = '<div class="text-rose-500 text-xs py-8 text-center">이 페이지를 렌더링할 수 없습니다.</div>';
        }
        
        pageInfo.innerText = `${pageNum + 1} / ${hwpPageCount}`;
        currentHwpPage = pageNum;
    } catch (err) {
        console.error("Failed to render page:", err);
        contentArea.innerHTML = `<div class="text-rose-500 text-xs py-8 text-center">렌더링 에러: ${err.message}</div>`;
    }
}

window.loadProject = function(id) {
    if (appState.loadProject(id)) {
        updateProjectDropdown();
        const state = appState.getState();
        if (state.activeMode === 'quickdoc') {
            qStep(state.quickDocStep || 1);
        } else {
            pStep(state.proposalStep || 1);
        }
    }
};

export function renderDashboard() {
    try {
        const g = document.getElementById('project-grid');
        const l = document.getElementById('project-list');
        if(!g || !l) {
            console.error("❌ renderDashboard failed: grid or list container not found");
            return;
        }
        
        const state = appState.getState();
        const cardText = state.activeMode === 'proposal' ? "새 스마트 문서 작성하기" : "새 프로젝트 생성하기";
        const viewMode = localStorage.getItem('dashboard_view_mode') || 'grid';
        
        console.log(`📊 renderDashboard starting for mode: ${state.activeMode}, view: ${viewMode}`);
    
    // UI Toggle
    if (viewMode === 'grid') {
        g.style.display = 'grid';
        l.style.display = 'none';
        g.classList.remove('hidden'); l.classList.add('hidden');
        document.getElementById('btn-view-grid')?.classList.add('bg-indigo-100', 'text-indigo-600');
        document.getElementById('btn-view-list')?.classList.remove('bg-indigo-100', 'text-indigo-600');
    } else {
        l.style.display = 'block';
        g.style.display = 'none';
        g.classList.add('hidden'); l.classList.remove('hidden');
        document.getElementById('btn-view-list')?.classList.add('bg-indigo-100', 'text-indigo-600');
        document.getElementById('btn-view-grid')?.classList.remove('bg-indigo-100', 'text-indigo-600');
    }

    const projects = Object.keys(state.projects || {})
        .map(id => ({ id, ...state.projects[id] }))
        .filter(p => {
            if (state.activeMode === 'proposal') {
                return (p.mode === 'proposal' || p.mode === 'quickdoc' || !p.mode);
            }
            return p.mode === state.activeMode;
        })
        .sort((a, b) => b.id.localeCompare(a.id));

    // Dashboard Description Update
    const modeDesc = document.getElementById('mode-desc');
    if (modeDesc) {
        if (state.activeMode === 'proposal') {
            modeDesc.innerText = "사용자 맞춤형 AI 스마트 문서 제작 워크플로우";
        } else {
            modeDesc.innerText = "나라장터 최신 공고를 검색하고 사업을 발굴합니다.";
        }
    }

    // Grid HTML
    let gridH = `<div id="btn-create-new-project-grid" style="min-height: 260px !important;" class="modern-card border-2 border-dashed border-indigo-100 bg-indigo-50/30 p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-indigo-50 hover:border-indigo-400 group transition-all duration-300 shadow-sm"><div class="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md text-indigo-500 group-hover:scale-110 group-hover:rotate-90 transition-transform mb-4 text-3xl font-light">+</div><p class="text-indigo-900 font-bold text-sm">${cardText}</p></div>`;
    
    // List HTML
    let listH = `<div id="btn-create-new-project-list" style="min-height: 80px !important;" class="flex items-center p-4 bg-indigo-50/50 border-2 border-dashed border-indigo-200 rounded-2xl cursor-pointer hover:bg-indigo-100 transition group mb-4"><div class="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-500 mr-4 font-bold shadow-sm group-hover:scale-110 transition-transform">+</div><p class="font-bold text-indigo-900">${cardText}</p></div>`;

    console.log(`📊 Rendering Dashboard: ${projects.length} projects found for mode ${state.activeMode}`);

    projects.forEach(p => {
        const id = p.id;
        let dStr = "최근 편집됨";
        if(id.startsWith('proj_')) {
            const dt = new Date(parseInt(id.split('_')[1]));
            if(!isNaN(dt.getTime())) dStr = `${dt.getFullYear()}.${String(dt.getMonth()+1).padStart(2,'0')}.${String(dt.getDate()).padStart(2,'0')}`;
        }
        
        const status = p.status || '진행';
        let statusClass = 'bg-blue-100 text-blue-600';
        let statusLabel = '진행 중';
        
        if (status === 'dropped') {
            statusClass = 'bg-rose-100 text-rose-600';
            statusLabel = 'Drop';
        } else if (status === '완료') {
            statusClass = 'bg-emerald-100 text-emerald-600';
            statusLabel = '완료';
        }
        
        const docType = p.documentType || 'proposal';
        let typeLabel = '📄 일반 문서';
        let docEmoji = '📄';
        if (docType === 'proposal') { typeLabel = '📝 B2B 제안서'; docEmoji = '📝'; }
        else if (docType === 'report') { typeLabel = '📊 보고서'; docEmoji = '📊'; }
        else if (docType === 'project-plan') { typeLabel = '💼 수행계획서'; docEmoji = '💼'; }
        else if (docType === 'edu-plan') { typeLabel = '🏫 교육계획안'; docEmoji = '🏫'; }
        else if (docType === 'meeting') { typeLabel = '✍️ 회의록'; docEmoji = '✍️'; }
        else if (docType === 'email') { typeLabel = '✉️ 이메일'; docEmoji = '✉️'; }
        else if (docType === 'blog') { typeLabel = '📰 블로그'; docEmoji = '📰'; }
        else if (docType === 'experience') { typeLabel = '🎒 체험학습'; docEmoji = '🎒'; }

        let thumb = `<div class="absolute inset-0 bg-gradient-to-br from-slate-100 to-indigo-50 flex flex-col items-center justify-center"><span class="text-5xl grayscale opacity-10 mb-2">${docEmoji}</span><p class="text-[10px] text-slate-300 font-bold tracking-widest uppercase">No Preview</p></div>`;
        if (p.slides && Object.keys(p.slides).length > 0) { 
            const slidePart = p.slides[Object.keys(p.slides)[0]]; 
            if (slidePart && slidePart.html) { 
                const [cw, ch] = (slidePart.size || "1411x794").split('x').map(Number);
                const sc = Math.min(280 / cw, 160 / ch); 
                const safeHtml = slidePart.html.replace(/"/g, '&quot;');
                const safeCss = (slidePart.css || "").replace(/"/g, '&quot;');
                const srcDoc = `<!DOCTYPE html><html><head><style>body{margin:0;padding:0;overflow:hidden;background:white;} ${safeCss}</style></head><body>${safeHtml}</body></html>`;
                thumb = `<div class="absolute inset-0 flex items-center justify-center bg-white overflow-hidden">
                            <div style="width:${cw}px; height:${ch}px; transform: scale(${sc}); transform-origin: top left; position: absolute; left: 50%; top: 50%; margin-left: -${cw * sc / 2}px; margin-top: -${ch * sc / 2}px;">
                                <iframe srcdoc="${srcDoc}" style="width:100%; height:100%; border:none; pointer-events: none;" scrolling="no"></iframe>
                            </div>
                         </div>
                         <div class="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 z-10"></div>`; 
            } 
        }

        gridH += `
            <div class="modern-card overflow-hidden cursor-pointer min-h-[280px] flex flex-col group relative border border-gray-100 hover:shadow-xl hover:border-indigo-200 transition-all duration-500">
                <div class="absolute top-3 right-3 z-30"><button onclick="window.deleteProject(event, '${id}')" class="w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 hover:scale-110">&times;</button></div>
                <div onclick="window.loadProject('${id}')" class="h-44 bg-slate-100 relative overflow-hidden group-hover:brightness-95 transition-all duration-500 w-full border-b border-gray-100">${thumb}</div>
                <div onclick="window.loadProject('${id}')" class="p-5 flex-1 flex flex-col justify-between bg-white relative z-20">
                    <div>
                        <div class="flex justify-between items-start mb-2.5">
                            <span class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusClass} border border-current opacity-80">${statusLabel}</span><span class="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200 ml-1.5">${typeLabel}</span>
                        </div>
                        <h3 class="font-extrabold text-slate-800 text-sm line-clamp-2 leading-snug group-hover:text-indigo-600 transition-colors">${p.name || '제목 없음'}</h3>
                    </div>
                    <div class="flex justify-between items-center mt-4">
                        <span class="text-[10px] font-bold text-slate-400 flex items-center gap-1"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>${dStr}</span>
                        <div class="w-6 h-6 rounded-lg bg-indigo-50 text-indigo-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7"></path></svg></div>
                    </div>
                </div>
            </div>`;

        listH += `
            <div class="bg-white border border-gray-100 rounded-2xl p-5 flex items-center justify-between hover:shadow-lg hover:border-indigo-100 transition-all duration-300 cursor-pointer group mb-3 shadow-sm" onclick="window.loadProject('${id}')">
                <div class="flex items-center gap-5">
                    <div class="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-2xl text-slate-400 group-hover:text-indigo-500 group-hover:bg-indigo-50 transition border border-gray-100 shadow-inner">${docEmoji}</div>
                    <div>
                        <h3 class="font-extrabold text-slate-800 text-base group-hover:text-indigo-600 transition-colors">${p.name || '제목 없음'}</h3>
                        <div class="flex items-center gap-3 mt-1.5">
                            <span class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusClass} border border-current opacity-80">${statusLabel}</span><span class="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200 ml-1.5">${typeLabel}</span>
                            <span class="text-[11px] text-slate-400 font-bold">${dStr}</span>
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <button onclick="window.deleteProject(event, '${id}')" class="w-10 h-10 flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover:opacity-100 text-2xl">&times;</button>
                </div>
            </div>`;
    });
    
    g.innerHTML = gridH;
    l.innerHTML = listH;
    
    ['btn-create-new-project-grid', 'btn-create-new-project-list'].forEach(id => {
        const btn = document.getElementById(id);
        if (btn) {
            btn.onclick = () => {
                newProject();
            };
        }
    });

    // View toggle listeners
    const gridToggle = document.getElementById('btn-view-grid');
    const listToggle = document.getElementById('btn-view-list');
    if (gridToggle) gridToggle.onclick = () => {
        localStorage.setItem('dashboard_view_mode', 'grid');
        renderDashboard();
    };
    if (listToggle) listToggle.onclick = () => {
        localStorage.setItem('dashboard_view_mode', 'list');
        renderDashboard();
    };

        // Final Visibility Force
        const grid = document.getElementById('project-grid');
        if (grid) {
            grid.style.opacity = '1';
            grid.style.visibility = 'visible';
        }
        console.log(`🏁 renderDashboard completed. Grid HTML length: ${grid?.innerHTML?.length || 0}`);
    } catch (e) {
        console.error("🔥 renderDashboard critical error:", e);
    }
}

function updateStepIndicators(n, maxStep, pipeline) {
    const pBar = pipeline.querySelector('#p-progress-bar');
    if (pBar) {
        pBar.style.width = {1:'0%', 2:'33%', 3:'66%', 4:'100%'}[n] || '0%';
    }

    const state = appState.getState();
    const docType = state.projectData?.documentType || 'proposal';
    const labelsMap = {
        'proposal': ['RFP 분석', '의사결정 보드', '승리 전략/목차', '슬라이드 렌더링'],
        'report': ['자료 분석', '핵심지표 도출', '본문 세부작성', '보고 피드백 반영'],
        'project-plan': ['요구분석', 'WBS 일정 설계', '수행계획 작성', '계획서 인쇄/다운'],
        'edu-plan': ['목표 수립', '핵심역량 매핑', '차시 지도안 기술', '교안/활동지 다운'],
        'default': ['1단계: 준비', '2단계: 계획', '3단계: 본문', '4단계: 완료']
    };
    const labels = labelsMap[docType] || labelsMap['proposal'];

    for(let i=1; i<=4; i++) {
        const cWrapper = pipeline.querySelector(`#btn-p-step-${i}`);
        const c = pipeline.querySelector(`#p-ind-${i}`);
        if (!c || !cWrapper) continue;
        
        const span = cWrapper.querySelector('span');
        if (span && labels[i - 1]) {
            span.innerText = labels[i - 1];
        }

        if (i > maxStep) {
            cWrapper.classList.add('cursor-not-allowed', 'opacity-50');
            cWrapper.classList.remove('group');
            c.className = "w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg shadow-md border-4 border-white bg-slate-200 text-slate-400";
        } else {
            cWrapper.classList.remove('cursor-not-allowed', 'opacity-50');
            cWrapper.classList.add('group');
            
            if(i < n) {
                c.className = "w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg shadow-md border-4 border-white bg-indigo-600 text-white";
                c.innerHTML = "✓";
            } else if(i === n) {
                c.className = "w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg shadow-md border-4 border-white bg-indigo-600 text-white ring-4 ring-indigo-100";
                c.innerHTML = i;
            } else {
                c.className = "w-12 h-12 rounded-full flex justify-center items-center font-bold text-lg shadow-md border-4 border-white bg-white text-slate-400 group-hover:scale-110 transition-transform cursor-pointer";
                c.innerHTML = i;
            }
        }
    }
}

export function pStep(n) {
    const state = appState.getState();
    if (state.activeMode !== 'proposal' && n !== 0) return; 
    
    if (n > 0 && n > state.proposalMaxStep) {
        return; // Prevent navigating beyond max step
    }
    
    appState.setState({ proposalStep: n });
    window.scrollTo(0, 0);
    
    const pipeline = document.getElementById('pipeline-proposal');
    if (!pipeline) return;
    document.getElementById('pipeline-quickdoc')?.classList.add('hidden');

    ['step-0', 'p-step-1', 'p-step-2', 'p-step-3', 'p-step-4'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('step-hidden');
    });
    
    const tDisp = document.getElementById('project-title-input');
    const modeDesc = document.getElementById('mode-desc');
    
    if(n === 0) { 
        const s0 = document.getElementById('step-0');
        if(s0) s0.classList.remove('step-hidden');
        const dView = document.getElementById('dashboard-view');
        if(dView) {
            dView.classList.remove('hidden');
        }
        if(tDisp) { 
            tDisp.value = "대시보드"; 
            tDisp.readOnly = true;
            tDisp.classList.remove('hover:bg-slate-50', 'cursor-pointer');
            tDisp.classList.add('cursor-default');
        }
        document.getElementById('project-title-edit-icon')?.classList.add('hidden');
        document.getElementById('btn-exit-project')?.classList.add('hidden');
        pipeline.classList.add('hidden');
        renderDashboard();
        return; 
    } else {
        document.getElementById('dashboard-view')?.classList.add('hidden');
        pipeline.classList.remove('hidden');
        if(tDisp) { 
            tDisp.value = state.projectData.name || "신규 프로젝트"; 
            tDisp.readOnly = false;
            tDisp.classList.remove('cursor-default');
            tDisp.classList.add('hover:bg-slate-50', 'cursor-pointer');
        }
        document.getElementById('project-title-edit-icon')?.classList.remove('hidden');
        document.getElementById('btn-exit-project')?.classList.remove('hidden');
        if (modeDesc) {
            const docType = state.projectData.documentType || 'proposal';
            const typeNames = { 'proposal': 'B2B 제안서', 'report': '보고서', 'project-plan': '수행계획서', 'edu-plan': '교육계획안' };
            const typeName = typeNames[docType] || '스마트 문서';
            const stepNames = ["", "1단계: 자료/요구 분석", "2단계: 핵심 의사결정", "3단계: 목차 및 내용 기획", "4단계: 슬라이드 렌더링"];
            modeDesc.innerText = `${typeName} 작성 - ${stepNames[n] || '진행'}`;
        }
    }
    
    const stepEl = pipeline.querySelector(`#p-step-${n}`);
    if (stepEl) stepEl.classList.remove('step-hidden');
    
    updateStepIndicators(n, state.proposalMaxStep, pipeline);

    if (n === 2) {
        const vm2 = document.getElementById('gonogo-view-mode'), im2 = document.getElementById('gonogo-input'); 
        if(vm2 && state.projectData.gonogo) vm2.innerHTML = marked.parse(state.projectData.gonogo); 
        if(im2 && state.projectData.gonogo) im2.value = state.projectData.gonogo;
    } else if (n === 3) {
        const vm3 = document.getElementById('strategy-view-mode'), im3 = document.getElementById('strategy-input'); 
        if(vm3 && state.projectData.strategy) vm3.innerHTML = marked.parse(state.projectData.strategy); 
        if(im3 && state.projectData.strategy) im3.value = state.projectData.strategy; 
        renderTOCEditor(); 
    } else if (n === 4) {
        updatePartSelectorOptions();
        window.prepareSlidePrompt(); // Added to fix empty placeholder issue
        loadSlidePreview();
    }

    // Defense against viewport scroll jumping
    window.scrollTo(0, 0);
}

function showLoader(title, desc) {
    if(document.getElementById('loader-title')) document.getElementById('loader-title').innerText = title; 
    if(document.getElementById('loader-desc')) document.getElementById('loader-desc').innerText = desc; 
    if(document.getElementById('global-loader')) document.getElementById('global-loader').classList.remove('modal-hidden'); 
}
function hideLoader() {
    if(document.getElementById('global-loader')) document.getElementById('global-loader').classList.add('modal-hidden'); 
}

const isRecoverableAIUpstreamError = (error = {}) => (
    error.code === 'FREELLM_SERVER_ERROR' ||
    error.code === 'FREELLM_TIMEOUT_ERROR' ||
    Number(error.upstreamStatus || 0) >= 500 ||
    /Status 50[0-9]|Upstream 서버 장애|timed out/i.test(error.message || '')
);

const compactForAI = (text = '', maxChars = 7000) => {
    const normalized = String(text)
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
    if (normalized.length <= maxChars) return normalized;
    const head = normalized.slice(0, Math.floor(maxChars * 0.72));
    const tail = normalized.slice(-Math.floor(maxChars * 0.28));
    return `${head}\n\n[중간 원문 일부 생략: FreeLLM upstream 안정화를 위한 압축 재시도]\n\n${tail}`;
};

const shouldUsePatentRag = (query = '') => (
    /특허|표준특허|기술|시스템|소프트웨어|SW|AI|인공지능|데이터|플랫폼|보안|통신|장비|장치|센서|모듈|알고리즘|솔루션|정보화|ISP|클라우드|전산/i.test(query)
);

async function callProposalAnalysisWithFallback(primaryPrompt, fallbackContext) {
    try {
        return await aiService.callAI(primaryPrompt, "", "PROMPT_PROPOSAL_ANALYSIS");
    } catch (error) {
        if (!isRecoverableAIUpstreamError(error)) throw error;

        showLoader("분석 재시도", "AI 서버가 긴 입력을 거부해 핵심 정보만 압축하여 재시도 중...");

        const system = [
            '너는 조달/RFP 제안서 검토 전문가다.',
            '입력된 공고 메타데이터와 추출 텍스트를 바탕으로 Go/No-Go 판단에 필요한 핵심만 작성한다.',
            '반드시 일반 마크다운으로 작성하고 LaTeX 수식 기호는 사용하지 않는다.'
        ].join('\n');
        const retryPrompt = [
            `[분석 포커스]: ${fallbackContext.focus}`,
            `[공고/RFP 압축 입력]:`,
            compactForAI(fallbackContext.rfpText),
            '',
            `[현황]: ${compactForAI(fallbackContext.context || '', 1500)}`,
            '',
            '[출력 형식]',
            '[프로젝트명]: 공고명 기반 프로젝트명',
            '1. 사업 개요',
            '2. 핵심 요구사항 및 산출물',
            '3. 입찰 참여 리스크',
            '4. 제안서 작성 유의사항',
            '5. Go/No-Go 권고와 근거'
        ].join('\n');

        return await aiService.callAI(retryPrompt, system, "PROMPT_PROPOSAL_ANALYSIS_RETRY_COMPACT");
    }
}

const getFileExtensionFromName = (name = '') => {
    const clean = String(name).split('?')[0].split('#')[0];
    return clean.includes('.') ? clean.split('.').pop().toLowerCase() : '';
};

const filenameFromDisposition = (disposition = '') => {
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    if (encoded) {
        try {
            return decodeURIComponent(encoded);
        } catch (e) {}
    }
    return disposition.match(/filename="([^"]+)"/i)?.[1] || '';
};

async function fetchSourcingAttachmentAsFile(attachment) {
    const proxyUrl = `/api/nara-attachment-proxy?url=${encodeURIComponent(attachment.url)}&name=${encodeURIComponent(attachment.name || '')}`;
    const response = await fetch(proxyUrl);
    if (!response.ok) {
        let detail = '';
        try {
            const data = await response.json();
            detail = data.error || data.detail || '';
        } catch (e) {}
        throw new Error(detail || `첨부 다운로드 실패 (${response.status})`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data?.success === false) {
            throw new Error(data.detail || data.error || '첨부 다운로드 실패');
        }
        throw new Error('첨부 다운로드가 파일 대신 JSON 응답을 반환했습니다.');
    }

    const blob = await response.blob();
    const dispositionName = filenameFromDisposition(response.headers.get('content-disposition') || '');
    let name = dispositionName || attachment.name || 'nara-attachment';
    const ext = getFileExtensionFromName(name);
    if (!ext && blob.type === 'application/pdf') name += '.pdf';
    return new File([blob], name, { type: blob.type || 'application/octet-stream' });
}

async function extractSourcingAttachments(detail = {}) {
    const attachments = (detail.attachments || []).filter(file => file?.url);
    if (attachments.length === 0) return;

    const extractionKey = `${detail.projectId || ''}:${attachments.map(file => `${file.name}|${file.url}`).join('::')}`;
    if (activeSourcingAttachmentExtractionKey === extractionKey) return;
    activeSourcingAttachmentExtractionKey = extractionKey;

    const statusLabel = document.getElementById('p-file-status');
    const sourceNotice = document.getElementById('p-rfp-source-notice');
    const sourceDetail = document.getElementById('p-rfp-source-detail');
    const textArea = document.getElementById('p-rfp-text');
    if (statusLabel) {
        statusLabel.innerText = `나라장터 첨부 ${attachments.length}개 추출 중...`;
        statusLabel.classList.remove('hidden');
    }
    if (sourceNotice && sourceDetail) {
        sourceNotice.classList.remove('hidden');
        sourceDetail.innerText = `공고 기본 정보는 이미 오른쪽 텍스트 영역에 입력되었습니다. 지금 나라장터 첨부 ${attachments.length}개의 본문을 추가 추출해 같은 입력값 뒤에 붙이고 있습니다.`;
    }

    const extracted = [];
    const failures = [];

    for (const attachment of attachments) {
        try {
            if (statusLabel) statusLabel.innerText = `나라장터 첨부 추출 중: ${attachment.name || '파일'}`;
            const file = await fetchSourcingAttachmentAsFile(attachment);
            const parsed = await FileParser.parseFiles([file]);
            if (parsed) extracted.push(parsed);
        } catch (err) {
            console.warn('[Sourcing Attachment] Extraction failed:', attachment, err);
            failures.push(`[Source: ${attachment.name || attachment.url}] (Remote parsing failed: ${err.message})`);
        }
    }

    const current = textArea?.value || '';
    const appended = [...extracted, ...failures].filter(Boolean).join('\n\n====\n\n');
    if (textArea && appended) {
        textArea.value = current
            ? `${current}\n\n====\n\n[나라장터 첨부 본문 자동 추출]\n${appended}`
            : `[나라장터 첨부 본문 자동 추출]\n${appended}`;
        appState.getState().projectData.rfp = textArea.value;
        appState.saveCurrentProject();
    }

    if (statusLabel) {
        const successCount = extracted.length;
        statusLabel.innerText = failures.length
            ? `첨부 추출 완료: ${successCount}개 성공, ${failures.length}개 실패`
            : `첨부 ${successCount}개 추출 완료`;
        setTimeout(() => statusLabel.classList.add('hidden'), 3500);
    }
    if (sourceNotice && sourceDetail) {
        const successCount = extracted.length;
        sourceDetail.innerText = failures.length
            ? `공고 기본 정보에 첨부 본문 추출 결과를 추가했습니다. 결과: ${successCount}개 성공, ${failures.length}개 실패. 실패한 파일은 왼쪽 업로드 영역에 직접 추가하면 분석 입력값에 반영됩니다.`
            : `공고 기본 정보에 나라장터 첨부 본문 ${successCount}개를 추가했습니다. 오른쪽 텍스트 영역 전체가 1단계 분석 입력값으로 사용됩니다.`;
    }
}

// ======================= Step 1 =========================
async function runStep1() {
    const state = appState.getState();
    const rfpText = document.getElementById('p-rfp-text')?.value.trim() || "";
    const context = document.getElementById('p-org-context')?.value.trim() || "";
    
    state.projectData.rfp = rfpText;
    state.projectData.context = context;
    
    if(!rfpText) return alert("데이터 입력 요망");
    
    // Check if the content is just parsing failure messages or too short
    const parts = rfpText.split('====');
    const validParts = parts.filter(p => !p.includes("(Parsing failed)") && !p.includes("⚠️") && p.trim().length > 20);
    
    if (validParts.length === 0) {
        return alert("유효한 분석 데이터가 없습니다.\n- 파일 파싱에 실패했거나 내용이 유효하지 않습니다.\n- HWP 등 바이너리 찌꺼기만 남은 경우 진행이 차단됩니다.\n\n데이터를 확인하거나 직접 텍스트를 입력해 주세요.");
    }
    
    if (rfpText.includes("(Parsing failed)") || rfpText.includes("⚠️")) {
        if (!confirm("일부 파일에서 데이터 추출에 실패했거나 노이즈가 감지되었습니다. 유효한 데이터만으로 분석을 진행하시겠습니까?")) {
            return;
        }
    }
    
    const focusBtn = document.querySelector('#p-step-1 .bg-indigo-600.text-white');
    const focus = focusBtn ? focusBtn.innerText : "기본 분석";
    
    showLoader("분석 실행", `${focus} 추출 중...`);
    try {
        const adminState = getAdminState();
        
        // --- 2-Stage RAG: Web Search & Semantic Reranking ---
        let enrichedWebContext = "";
        try {
            // Extract clean search query from RFP title or prefix content
            const cleanQuery = (state.projectData.name && state.projectData.name !== '대시보드' && state.projectData.name !== '신규 제안') 
                ? state.projectData.name 
                : rfpText.slice(0, 120).replace(/[^가-힣a-zA-Z0-9\s]/g, ' ').trim();
            
            if (cleanQuery) {
                console.log(`[RAG 2-Step] Web fetching for context query: "${cleanQuery}"`);
                const webResults = await ragService.searchWebContext(cleanQuery, adminState.tavilyApiKey);
                const reranked = await ragService.rerankDocuments(cleanQuery, webResults);
                
                if (reranked && reranked.length > 0) {
                    enrichedWebContext = "\n\n### 📡 [RAG 실시간 외부 시장 및 기술 동향 결합]\n" + 
                        reranked.slice(0, 3).map((r, idx) => `[동향 ${idx + 1}] ${r.title}\n- 내용: ${r.content}\n- 출처: ${r.url}`).join('\n\n');
                }

                if (shouldUsePatentRag(cleanQuery)) {
                    // 3-Stage RAG: Patent API integration
                    try {
                        console.log(`[RAG Patent] Querying KIPO Std Patents for: "${cleanQuery}"`);
                        const patentInfo = await aiService.searchPatentFromAPI(cleanQuery);
                        if (patentInfo) {
                            enrichedWebContext += "\n\n### 🛡️ [특허청 표준 특허 기술 지식 결합]\n" + patentInfo;
                        }
                    } catch (patErr) {
                        console.warn("[RAG Patent] Bypassed patent enrichment:", patErr.message);
                    }
                } else {
                    console.info(`[RAG Patent] Skipped for non-technical sourcing query: "${cleanQuery}"`);
                }
            }
        } catch (ragErr) {
            console.warn("[RAG 2-Step] Search enrichment bypassed due to:", ragErr.message);
        }
        
        const p1Prompt = adminState.prompts?.PROMPT_PROPOSAL_ANALYSIS || "";
        const p = `[분석 포커스]: ${focus}\n[RFP]: ${rfpText}\n[현황]: ${context}${enrichedWebContext}\n\n${p1Prompt}`; 
        const result = await callProposalAnalysisWithFallback(p, {
            focus,
            rfpText,
            context: `${context}${enrichedWebContext}`
        }); 
        if (!result) throw new Error("AI 응답을 생성하지 못했습니다. 다시 시도해 주세요.");
        state.projectData.gonogo = result; 
        
        // Robust Precautions (유의사항) Extraction
        const precMatch = result.match(/4\.\s*제안서\s*작성\s*유의사항([\s\S]*)/i);
        if (precMatch && precMatch[1]) {
            state.projectData.precautions = precMatch[1].trim();
        } else {
            const fallbackMatch = result.match(/###\s*유의사항([\s\S]*)/i);
            state.projectData.precautions = fallbackMatch ? fallbackMatch[1].trim() : "RFP 분석 결과 특별한 유의사항이 도출되지 않았습니다.";
        }
        
        const m = result.match(/\[프로젝트명\]:\s*(.*)/); 
        state.projectData.name = m ? m[1].replace(/[*#]/g,'').trim() : "신규 제안"; 
        
        if(document.getElementById('project-title-input')) document.getElementById('project-title-input').value = state.projectData.name; 
        
        appState.saveCurrentProject(); // Save first to ensure name is persisted
        updateProjectDropdown(); // Refresh GNB dropdown
        
        appState.setState({ proposalMaxStep: Math.max(state.proposalMaxStep, 2) });
        pStep(2); 
    } catch(e) { 
        console.error("Step 1 failed:", e);
        alert("분석 실패: " + e.message); 
    } finally { 
        hideLoader(); 
    }
}

// ======================= Step 2 =========================
function toggleGonogoEdit() {
    const v = document.getElementById('gonogo-view-mode'), i = document.getElementById('gonogo-input'), b = document.getElementById('btn-gonogo-edit'); 
    if(!v || !i) return; 
    if(i.classList.contains('hidden')) { 
        i.value = appState.getState().projectData.gonogo || ""; 
        v.classList.add('hidden'); i.classList.remove('hidden'); i.focus(); 
        if(b) b.innerHTML = "💾 저장"; 
    } else { 
        appState.getState().projectData.gonogo = i.value; 
        if(v) v.innerHTML = marked.parse(appState.getState().projectData.gonogo); 
        v.classList.remove('hidden'); i.classList.add('hidden'); 
        if(b) b.innerHTML = "✏️ 편집"; 
        appState.saveCurrentProject();
    }
}

async function runStep2() {
    const state = appState.getState();
    showLoader("전략 수립", "생성 중..."); 
    try { 
        const adminState = getAdminState();
        const p2Prompt = adminState.prompts?.PROMPT_PROPOSAL_TOC || "";
        const p = `[RFP 요약]: ${state.projectData.rfp.substring(0,3000)}\n[이전 분석]: ${state.projectData.gonogo}\n\n${p2Prompt}`; 
        const result = await aiService.callAI(p, "", "PROMPT_PROPOSAL_TOC"); 
        state.projectData.strategy = result; 
        parseMarkdownToTOC(result);
        
        appState.setState({ proposalMaxStep: Math.max(state.proposalMaxStep, 3) });
        appState.saveCurrentProject(); 
        pStep(3); 
    } catch(e) { 
        alert("실패: " + e.message); 
    } finally { 
        hideLoader(); 
    }
}

// ======================= Step 3 =========================
function parseMarkdownToTOC(md) { 
    const state = appState.getState();
    state.projectData.toc = []; 
    if(!state.projectData.customCols) state.projectData.customCols = []; 
    const lines = md.split('\n'); 
    let inT = false; 
    let colMap = { lv: 1, title: 2, page: 3, own: 4, stat: 5, det: 6 };
    const c = function(s) { return s.replace(/[\*\"\']/g, '').trim(); }; 

    for(let l of lines) { 
        if(l.trim().startsWith('|')) { 
            if(l.includes('---')) continue; 
            if(l.includes('레벨') || l.includes('목차명')) { 
                inT = true; 
                const headers = l.split('|').map(s => s.trim());
                headers.forEach((h, idx) => {
                    if (h.includes('레벨')) colMap.lv = idx;
                    if (h.includes('목차')) colMap.title = idx;
                    if (h.includes('분량') || h.includes('페이지') || h.includes('(P)')) colMap.page = idx;
                    if (h.includes('담당')) colMap.own = idx;
                    if (h.includes('상태')) colMap.stat = idx;
                    if (h.includes('세부') || h.includes('내용')) colMap.det = idx;
                });
                continue; 
            } 
            if(inT) { 
                const cols = l.split('|').map(function(s) { return s.trim(); }); 
                if(cols.length >= 5) { 
                    let lv = c(cols[colMap.lv] || "");
                    let ti = c(cols[colMap.title] || "");
                    let pg = c(cols[colMap.page] || "1").replace(/[^0-9]/g, '');
                    
                    // Filter LaTeX/Math artifacts from title and level
                    ti = ti.replace(/[\$\\]/g, '').replace(/rightarrow/g, '->');
                    lv = lv.replace(/[\$\\]/g, '').replace(/rightarrow/g, '->');

                    // Standardize logical flow levels if they follow the Why/What/How pattern
                    if (ti.includes("배경") || ti.includes("필요성") || ti.includes("비전")) lv = "I-" + lv.replace(/[^0-9]/g, '');
                    if (ti.includes("설계") || ti.includes("방안") || ti.includes("기술")) lv = "II-" + lv.replace(/[^0-9]/g, '');
                    if (ti.includes("관리") || ti.includes("로드맵") || ti.includes("일정")) lv = "III-" + lv.replace(/[^0-9]/g, '');

                    const own = c(cols[colMap.own] || "");
                    const stat = c(cols[colMap.stat] || "작성전");
                    const det = c(cols[colMap.det] || "");

                    let r = { 
                        level: lv, 
                        title: ti, 
                        page: pg || "1", 
                        owner: own, 
                        status: stat.includes('작성') ? stat : "⚪️ 작성전", 
                        detail: det, 
                        custom: {} 
                    }; 
                    state.projectData.customCols.forEach(function(col) { r.custom[col.id] = ""; }); 
                    state.projectData.toc.push(r); 
                } 
            } 
        } else if(inT && l.trim() === '') inT = false; 
    } 
    // Clean up weird placeholders like 1362618318
    state.projectData.toc = state.projectData.toc.filter(t => !t.title.includes('1362618318'));
    
    if(state.projectData.toc.length === 0) {
        state.projectData.toc.push({level:"I-1", title:"제안의 배경 및 목적", page:"2", owner:"", status:"작성전", detail:"Why: 문제 제기 및 비전 제시", custom:{}});
        state.projectData.toc.push({level:"II-1", title:"사업 수행 방안", page:"5", owner:"", status:"작성전", detail:"What: 구체적인 미래 설계", custom:{}});
        state.projectData.toc.push({level:"III-1", title:"사업 관리 및 로드맵", page:"3", owner:"", status:"작성전", detail:"How: 실행 로드맵 및 관리 계획", custom:{}});
    }
}

// TOC logic moved to explicit window functions below to avoid duplication

function renderTOCEditor() { 
    const state = appState.getState();
    const th = document.querySelector('.toc-table thead tr'), tb = document.querySelector('.toc-table tbody'); 
    if(!th || !tb) return; 
    
    // Header - Standardizing columns to match index2.html
    let h = `<th class="px-3 py-3 w-16 rounded-tl-lg">레벨</th>
             <th class="px-3 py-3 w-48">목차명</th>
             <th class="px-3 py-3 w-16">분량</th>
             <th class="px-3 py-3 w-24">담당자</th>
             <th class="px-3 py-3 w-24">상태</th>
             <th class="px-3 py-3 min-w-[200px]">세부내용</th>`; 
             
    if (state.projectData.customCols) {
        state.projectData.customCols.forEach(col => {
            h += `<th class="px-3 py-3 min-w-[100px] relative group">
                    <input type="text" value="${col.name}" onchange="window.updateCustomColName('${col.id}', this.value)" class="w-full bg-transparent font-bold text-slate-700 outline-none p-1 rounded">
                    <button onclick="window.removeCustomColumn('${col.id}')" class="absolute top-2 right-2 text-rose-500 opacity-0 group-hover:opacity-100 bg-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm hover:bg-rose-50">&times;</button>
                  </th>`;
        });
    }
    h += `<th class="px-3 py-3 text-center w-24 rounded-tr-lg">액션</th>`;
    th.innerHTML = h; 
    
    // Body
    tb.innerHTML = ''; 
    state.projectData.toc.forEach(function(r, i) { 
        const tr = document.createElement('tr'); 
        tr.className = "border-b border-slate-100 hover:bg-slate-50 transition"; 
        const isB = r.level.match(/^[1I가]/) ? "font-bold text-indigo-900" : "text-slate-700"; 
        
        let rH = `<td><input value="${r.level}" onchange="window.updateTOC(${i},'level',this.value)" class="text-center font-bold outline-none bg-transparent w-full"></td>
                  <td><input value="${r.title}" onchange="window.updateTOC(${i},'title',this.value)" class="${isB} outline-none bg-transparent w-full"></td>
                  <td><input value="${r.page}" onchange="window.updateTOC(${i},'page',this.value)" class="text-center outline-none bg-transparent w-full"></td>
                  <td><input value="${r.owner || ''}" onchange="window.updateTOC(${i},'owner',this.value)" class="text-center text-xs outline-none bg-transparent w-full"></td>
                  <td><select onchange="window.updateTOC(${i},'status',this.value); window.updatePartSelectorOptions(); appState.saveCurrentProject();" class="w-full text-center text-xs font-bold outline-none cursor-pointer bg-transparent ${r.status.includes('완료') ? 'text-emerald-600' : 'text-slate-500'}">
                    <option value="작성전" ${r.status==='작성전'?'selected':''}>⚪️ 작성전</option>
                    <option value="작성중" ${r.status==='작성중'?'selected':''}>📝 작성중</option>
                    <option value="검토요청" ${r.status==='검토요청'?'selected':''}>👀 검토요청</option>
                    <option value="작성완료" ${r.status==='작성완료'?'selected':''}>✅ 작성완료</option>
                  </select></td>
                  <td><input value="${r.detail || ''}" onchange="window.updateTOC(${i},'detail',this.value)" class="text-xs text-slate-500 outline-none bg-transparent w-full"></td>`; 
        
        if (state.projectData.customCols) {
            state.projectData.customCols.forEach(col => {
                const val = (r.custom && r.custom[col.id]) || "";
                rH += `<td><input value="${val}" onchange="window.updateTOC(${i},'custom',this.value, '${col.id}')" class="text-xs outline-none bg-transparent w-full"></td>`;
            });
        }
        
        // Actions: Up, Down, Delete
        rH += `<td class="text-center whitespace-nowrap">
                <button onclick="window.moveTOCRow(${i},-1)" class="text-slate-400 hover:text-indigo-600 px-1 transition-colors">▲</button>
                <button onclick="window.moveTOCRow(${i},1)" class="text-slate-400 hover:text-indigo-600 px-1 transition-colors">▼</button>
                <button onclick="window.removeTOCRow(${i})" class="text-rose-400 hover:text-rose-600 ml-1 text-lg transition-colors">&times;</button>
               </td>`; 
        tr.innerHTML = rH; 
        tb.appendChild(tr); 
    }); 
}

window.updateTOC = function(idx, field, val, customColId = null) {
    const state = appState.getState();
    if (field === 'custom' && customColId) {
        if (!state.projectData.toc[idx].custom) state.projectData.toc[idx].custom = {};
        state.projectData.toc[idx].custom[customColId] = val;
    } else {
        state.projectData.toc[idx][field] = val;
    }
    appState.saveCurrentProject();
};

window.addTOCRow = function() {
    const state = appState.getState();
    if (!state.projectData.toc) state.projectData.toc = [];
    state.projectData.toc.push({ level: "1", title: "신규 목차", page: "1", owner: "", status: "작성전", detail: "", custom: {} });
    renderTOCEditor();
    appState.saveCurrentProject();
};

window.removeTOCRow = function(idx) {
    const state = appState.getState();
    if (confirm("이 행을 삭제하시겠습니까?")) {
        state.projectData.toc.splice(idx, 1);
        renderTOCEditor();
        appState.saveCurrentProject();
    }
};

window.moveTOCRow = function(idx, dir) {
    const state = appState.getState();
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= state.projectData.toc.length) return;
    
    const temp = state.projectData.toc[idx];
    state.projectData.toc[idx] = state.projectData.toc[targetIdx];
    state.projectData.toc[targetIdx] = temp;
    
    renderTOCEditor();
    appState.saveCurrentProject();
};

window.addCustomColumn = function() {
    const state = appState.getState();
    const id = 'col_' + Date.now();
    if (!state.projectData.customCols) state.projectData.customCols = [];
    state.projectData.customCols.push({ id, name: "새 필드" });
    state.projectData.toc.forEach(r => {
        if (!r.custom) r.custom = {};
        r.custom[id] = "";
    });
    renderTOCEditor();
    appState.saveCurrentProject();
};

window.removeCustomColumn = function(id) {
    if(!confirm("이 컬럼과 관련 데이터를 모두 삭제하시겠습니까?")) return;
    const state = appState.getState();
    state.projectData.customCols = state.projectData.customCols.filter(c => c.id !== id);
    state.projectData.toc.forEach(row => {
        if(row.custom) delete row.custom[id];
    });
    renderTOCEditor();
    appState.saveCurrentProject();
};


window.rewriteStrategy = async function() {
    const state = appState.getState();
    showLoader("AI 재작성", "고도화 중...");
    try {
        const p = `[분석 결과]: ${state.projectData.gonogo}\n[현재 전략]: ${state.projectData.strategy}\n\n위 내용을 바탕으로 경쟁사를 압도할 수 있는 더 구체적인 승리 전략(Win-Strategy)으로 고도화하라.`;
        const res = await aiService.callAI(p, "", "PROMPT_PROPOSAL_STRATEGY_REWRITE");
        state.projectData.strategy = res;
        appState.saveCurrentProject();
        pStep(3);
    } catch(e) { alert(e.message); } finally { hideLoader(); }
};

// ======================= Step 4 =========================
window.updatePartSelectorOptions = function() { 
    const state = appState.getState();
    const s = document.getElementById('toc-selector'); 
    if(s && state.projectData.toc) { 
        const c = s.value; s.innerHTML = ''; 
        state.projectData.toc.forEach(function(t) { 
            const d = state.projectData.slides && state.projectData.slides[t.title]; 
            const i = d ? "✅ " : (t.status === '작성완료' ? "✅ " : (t.status === '검토요청' ? "👀 " : (t.status === '작성중' ? "📝 " : "⚪️ "))); 
            const p = t.page ? String(t.page).replace(/[^0-9]/g, '') : '1'; 
            s.innerHTML += `<option value="${t.title}">${i} [${t.level}] ${t.title} (${p||'1'}장)</option>`; 
        }); 
        if(c) s.value = c; 
    } 
    
    // Update template selector
    const ts = document.getElementById('template-selector');
    if (ts) {
        const adminState = getAdminState();
        if (adminState.templates) {
            const currentTpl = ts.value;
            ts.innerHTML = adminState.templates.map(t => `<option value="${t.id}">${t.name}</option>`).join('');
            if (currentTpl && Array.from(ts.options).some(o => o.value === currentTpl)) {
                ts.value = currentTpl;
            } else if (adminState.templates.length > 0) {
                ts.value = adminState.templates[0].id;
            }
        }
    }
};

window.loadSlidePreview = function(pageIdx = 0) {
    const state = appState.getState();
    const tSel = document.getElementById('toc-selector');
    const tN = tSel?.value;
    const sD = state.projectData.slides ? state.projectData.slides[tN] : null; 
    const rB = document.getElementById('slide-preview-container');
    const pI = document.getElementById('slide-page-info');
    const headerActions = document.getElementById('slide-actions-header');
    
    if(!sD || !sD.html) { 
        if(headerActions) headerActions.classList.add('hidden');
        if(rB) {
            rB.innerHTML = `
                <div id="slide-empty-state" class="text-slate-400 font-medium text-center">
                    <span class="text-3xl opacity-50 block mb-4">🖼️</span> 
                    <p class="text-lg font-bold text-slate-500 mb-1">렌더링된 슬라이드가 없습니다</p>
                    <p class="text-sm">상단 프롬프트를 확인하고 [슬라이드 생성] 버튼을 클릭하세요.</p>
                </div>`;
        }
        if(pI) pI.innerText = "PAGE 1 / 1";
        return; 
    }
    
    if(headerActions) headerActions.classList.remove('hidden');

    // Robust Slide Splitting
    const rawHtml = sD.html;
    let slides = [];
    const parts = rawHtml.split(/<div\s+class=["']slide["']/gi);
    if (parts.length > 1) {
        for (let i = 1; i < parts.length; i++) {
            let slideContent = '<div class="slide"' + parts[i];
            if (!slideContent.trim().endsWith('</div>')) {
                if (slideContent.includes('</div>')) {
                    const lastIdx = slideContent.lastIndexOf('</div>');
                    slideContent = slideContent.substring(0, lastIdx + 6);
                } else {
                    slideContent += '</div>';
                }
            }
            slides.push(slideContent);
        }
    } else {
        slides = [rawHtml]; 
    }
    
    const total = slides.length;
    const idx = Math.max(0, Math.min(pageIdx, total - 1));
    window.currentSlidePageIdx = idx;
    if(pI) pI.innerText = `PAGE ${idx + 1} / ${total}`;
    
    const [sw, sh] = (sD.size || "1411x794").split('x');
    const containerWidth = rB.clientWidth - 48; 
    const containerHeight = rB.clientHeight - 48;
    
    if (containerWidth <= 0 || containerHeight <= 0) {
        setTimeout(() => loadSlidePreview(idx), 100);
        return;
    }
    
    const scale = Math.min(rB.clientWidth / sw, rB.clientHeight / sh, 1) * 0.95;
    
    if(rB) {
        const css = sD.css || "";
        const html = slides[idx] || "";
        const srcDoc = `<!DOCTYPE html><html><head><style>
            body{margin:0;padding:0;overflow:hidden;background:#f1f5f9;display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;} 
            #slide-container{width:${sw}px;height:${sh}px;background:white;position:relative;box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);transform-origin:center center;}
            ${css}
            .slide { width: 100% !important; height: 100% !important; margin: 0 !important; box-sizing: border-box; position: relative; overflow: hidden; }
        </style></head><body><div id="slide-container">${html}</div></body></html>`;

        rB.innerHTML = `
            <div style="transform: scale(${scale}); transform-origin: center center; width: ${sw}px; height: ${sh}px; overflow: hidden; background: #fff; border: 1px solid #cbd5e1; border-radius: 4px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);" class="relative">
                <iframe srcdoc="${srcDoc.replace(/"/g, '&quot;')}" style="width:100%; height:100%; border:none;" scrolling="no"></iframe>
            </div>`;
    }
};

window.openSlidePopup = function() {
    const state = appState.getState();
    const tSel = document.getElementById('toc-selector');
    const tN = tSel?.value;
    const sD = state.projectData.slides ? state.projectData.slides[tN] : null; 
    if (!sD || !sD.html) return alert("데이터가 없습니다."); 
    
    const [w, h] = (sD.size || "1411x794").split('x');
    const win = window.open('', '_blank', `width=${parseInt(w) + 100},height=${parseInt(h) + 150},scrollbars=no,resizable=yes`); 
    if (!win) return alert("팝업 차단 해제 요망"); 
    
    const cH = sD.html.replace(/`/g, '\\`').replace(/\$/g, '\\$');
    const css = sD.css || "";
    
    win.document.write(`<!DOCTYPE html><html><head><title>[${tN}] - AuraDoc Viewer</title><style>
        body{background:#0f172a;margin:0;display:flex;flex-direction:column;height:100vh;overflow:hidden;font-family:'Pretendard', sans-serif;}
        #tgt{flex:1;display:flex;align-items:center;justify-content:center;position:relative;perspective:1000px;}
        .sw{width:${w}px;height:${h}px;background:white;position:absolute;transform-origin:center center;box-shadow:0 30px 60px -12px rgba(0,0,0,0.5);transition:opacity 0.3s;}
        .ctls{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);display:flex;gap:15px;align-items:center;background:rgba(15, 23, 42, 0.8);backdrop-filter:blur(12px);padding:8px 24px;border-radius:40px;z-index:1000;border:1px solid rgba(255,255,255,0.1);box-shadow:0 10px 30px rgba(0,0,0,0.5);}
        button{cursor:pointer;padding:6px 16px;font-weight:700;background:rgba(255,255,255,0.1);color:white;border:1px solid rgba(255,255,255,0.2);border-radius:10px;transition:all 0.2s;font-size:11px;text-transform:uppercase;}
        button:hover{background:rgba(255,255,255,0.2);border-color:rgba(255,255,255,0.4);}
        button:disabled{opacity:0.2;cursor:not-allowed;}
        #pT{font-size:13px;color:white;font-weight:800;min-width:50px;text-align:center;font-variant-numeric: tabular-nums;}
        .exit-btn{background:#e11d48!important;border-color:#fb7185!important;}
        ${css}
        .slide{width:100%!important;height:100%!important;margin:0!important;box-sizing:border-box;position:relative;overflow:hidden;}
    </style></head><body>
    <div id="tgt"><div class="sw" id="ctn"></div></div>
    <div class="ctls" id="cb" style="display:none;">
        <button id="pB" onclick="window.ch(-1)">PREV</button>
        <span id="pT">1 / 1</span>
        <button id="nB" onclick="window.ch(1)">NEXT</button>
        <button class="exit-btn" onclick="window.close()">CLOSE</button>
    </div>
    <script>
        const tm=document.createElement('div');
        tm.innerHTML=\`${cH}\`;
        const s=Array.from(tm.querySelectorAll('.slide'));
        let c=0;
        const cn=document.getElementById('ctn');
        function rs(){
            const sc = Math.min((window.innerWidth-80)/${w},(window.innerHeight-120)/${h});
            cn.style.transform='scale('+sc+')';
        }
        window.addEventListener('resize',rs);
        rs();
        if(s.length<=1){
            cn.innerHTML=\`${cH}\`;
            document.getElementById('cb').style.display='flex';
            document.getElementById('pB').style.display='none';
            document.getElementById('nB').style.display='none';
            document.getElementById('pT').style.display='none';
        }else{
            document.getElementById('cb').style.display='flex';
            window.ch=function(d){
                c+=d;
                if(c<0)c=0;
                if(c>=s.length)c=s.length-1;
                rd();
            };
            function rd(){
                cn.style.opacity='0';
                setTimeout(() => {
                    cn.innerHTML='';
                    cn.appendChild(s[c].cloneNode(true));
                    document.getElementById('pT').innerText=(c+1)+" / "+s.length;
                    document.getElementById('pB').disabled=(c===0);
                    document.getElementById('nB').disabled=(c===s.length-1);
                    cn.style.opacity='1';
                }, 100);
            }
            rd();
        }
    </script></body></html>`); 
    win.document.close();
};

window.viewAllSlides = function(fP = false) {
    const state = appState.getState();
    if (!state.projectData.slides || Object.keys(state.projectData.slides).length === 0) return alert("데이터가 없습니다."); 
    
    let aH = "", css = "", w = 1411, h = 794;
    state.projectData.toc.forEach(function(t) { 
        const sD = state.projectData.slides[t.title]; 
        if (sD) { 
            aH += sD.html; 
            css = sD.css; 
            if (sD.size) [w, h] = sD.size.split('x').map(Number); 
        } 
    });
    
    if (!aH) return alert("생성된 슬라이드가 없습니다.");

    const win = window.open('', '_blank', `width=${screen.width},height=${screen.height},scrollbars=no,resizable=yes`); 
    if (!win) return alert("팝업 차단 해제 요망"); 
    
    const cH = aH.replace(/`/g, '\\`').replace(/\$/g, '\\$');
    
    if (fP) { 
        win.document.write(`<!DOCTYPE html><html><head><title>PDF Export - ${state.projectData.name}</title><style>
            body{background:#f1f5f9;margin:0;padding:20px;display:flex;flex-direction:column;align-items:center;gap:20px;}
            .sw{width:${w}px;height:${h}px;background:white;position:relative;overflow:hidden;flex-shrink:0;box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);} 
            ${css} 
            .slide{width:100%!important;height:100%!important;margin:0!important;position:relative;overflow:hidden;} 
            @media print{
                body{padding:0;background:white;display:block;}
                .sw{page-break-after:always;margin:0;box-shadow:none;}
                @page{size:${w}px ${h}px landscape;margin:0;}
            }
        </style></head><body><script>
            const t=document.createElement('div');
            t.innerHTML=\`${cH}\`;
            Array.from(t.querySelectorAll('.slide')).forEach(function(x){
                const w=document.createElement('div');
                w.className='sw';
                w.appendChild(x.cloneNode(true));
                document.body.appendChild(w);
            });
            setTimeout(function(){ window.print(); }, 1000);
        </script></body></html>`); 
    } else { 
        win.document.write(`<!DOCTYPE html><html><head><title>AuraDoc Slideshow - ${state.projectData.name}</title><style>
            body{background:#0f172a;margin:0;display:flex;flex-direction:column;height:100vh;overflow:hidden;font-family:'Pretendard', sans-serif;}
            #tgt{flex:1;display:flex;align-items:center;justify-content:center;position:relative;perspective:1000px;}
            .sw{width:${w}px;height:${h}px;background:white;position:absolute;transform-origin:center center;box-shadow:0 30px 60px -12px rgba(0,0,0,0.5);transition:opacity 0.3s;}
            .ctls{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);display:flex;gap:20px;align-items:center;background:rgba(15, 23, 42, 0.85);backdrop-filter:blur(15px);padding:10px 32px;border-radius:40px;z-index:1000;border:1px solid rgba(255,255,255,0.1);box-shadow:0 15px 40px rgba(0,0,0,0.5);}
            button{cursor:pointer;padding:8px 20px;font-weight:700;background:rgba(255,255,255,0.05);color:white;border:1px solid rgba(255,255,255,0.15);border-radius:12px;transition:all 0.2s;text-transform:uppercase;letter-spacing:1px;font-size:11px;}
            button:hover{background:rgba(255,255,255,0.15);border-color:rgba(255,255,255,0.3);transform:translateY(-1px);}
            button:active{transform:translateY(0);}
            button:disabled{opacity:0.2;cursor:not-allowed;}
            #pT{color:white;font-weight:800;min-width:80px;text-align:center;font-size:15px;font-variant-numeric: tabular-nums;}
            ${css} 
            .slide{width:100%!important;height:100%!important;margin:0!important;position:relative;overflow:hidden;}
            .exit-btn{background:#e11d48!important;border-color:#fb7185!important;margin-left:20px;}
            .exit-btn:hover{background:#be123c!important;}
            .fs-btn{background:#059669!important;border-color:#34d399!important;}
        </style></head><body>
        <div id="tgt"><div class="sw" id="ctn"></div></div>
        <div class="ctls" id="cb">
            <button id="pB" onclick="window.ch(-1)">PREV</button>
            <span id="pT">1 / 1</span>
            <button id="nB" onclick="window.ch(1)">NEXT</button>
            <button class="fs-btn" onclick="window.fs()">FULL SCREEN</button>
            <button class="exit-btn" onclick="window.close()">EXIT</button>
        </div>
        <script>
            const tm=document.createElement('div');
            tm.innerHTML=\`${cH}\`;
            const s=Array.from(tm.querySelectorAll('.slide'));
            let c=0;
            const cn=document.getElementById('ctn');
            function rs(){
                const sc = Math.min((window.innerWidth-60)/${w},(window.innerHeight-120)/${h});
                cn.style.transform='scale('+sc+')';
            }
            window.addEventListener('resize',rs);
            rs();
            window.ch=function(d){
                c+=d;
                if(c<0)c=0;
                if(c>=s.length)c=s.length-1;
                rd();
            };
            window.fs=function(){
                if (!document.fullscreenElement) {
                    document.documentElement.requestFullscreen();
                } else {
                    if (document.exitFullscreen) {
                        document.exitFullscreen();
                    }
                }
            };
            function rd(){
                cn.style.opacity='0';
                cn.style.transform += ' translateY(20px)';
                setTimeout(() => {
                    cn.innerHTML='';
                    cn.appendChild(s[c].cloneNode(true));
                    document.getElementById('pT').innerText=(c+1)+" / "+s.length;
                    document.getElementById('pB').disabled=(c===0);
                    document.getElementById('nB').disabled=(c===s.length-1);
                    cn.style.opacity='1';
                    rs();
                }, 150);
            }
            document.addEventListener('keydown', (e) => {
                if(e.key === 'ArrowRight' || e.key === ' ') window.ch(1);
                if(e.key === 'ArrowLeft') window.ch(-1);
                if(e.key === 'Escape') window.close();
                if(e.key.toLowerCase() === 'f') window.fs();
            });
            rd();
        </script></body></html>`); 
    } 
    win.document.close();
};

window.printToPDF = function() { window.viewAllSlides(true); };
window.updateCustomColName = function(id, name) {
    const state = appState.getState();
    const col = state.projectData.customCols.find(c => c.id === id);
    if(col) col.name = name.trim();
    appState.saveCurrentProject();
};

window.generateSlide = async function() { 
    const state = appState.getState();
    const tSel = document.getElementById('toc-selector'); 
    const tpSel = document.getElementById('template-selector'); 
    const promptEl = document.getElementById('slide-prompt-textarea');
    
    if(!tSel || !promptEl || !tpSel) return alert("설정 확인 요망."); 
    const tN = tSel.value; 
    let p = promptEl.value;
    
    if (!p) {
        await window.prepareSlidePrompt();
        p = promptEl.value;
        if (!p) return alert("프롬프트가 유효하지 않습니다.");
    }
    
    showLoader(`'${tN}' 렌더링`, "분량에 맞춰 자동 생성 중..."); 
    try { 
        const res = await aiService.callAI(p, "", `PROMPT_PROPOSAL_SLIDE_RENDER_${tN}`); 
        
        // Robust Extraction
        let fH = "";
        const codeBlockMatch = res.match(/```(?:html)?\s*([\s\S]*?)```/i);
        if (codeBlockMatch) {
            fH = codeBlockMatch[1].trim();
        } else {
            // Fallback: try to find the first <div class="slide" and last </div>
            const startIdx = res.indexOf('<div class="slide"');
            const endIdx = res.lastIndexOf('</div>');
            if (startIdx > -1 && endIdx > startIdx) {
                fH = res.substring(startIdx, endIdx + 6).trim();
            } else {
                fH = res.trim();
            }
        }
        
        // Final cleanup for markdown remnants and LaTeX
        fH = fH.replace(/```html\s*/gi, "").replace(/```\s*/g, "").trim();
        fH = fH.replace(/\\rightarrow/g, '->').replace(/\$\\rightarrow\$/g, '->');
        
        if (!fH || fH.length < 20 || !fH.includes('<div')) throw new Error("AI가 유효한 HTML 코드를 생성하지 못했습니다. 다시 시도해 주세요.");
        if(!state.projectData.slides) state.projectData.slides = {};
        
        const tId = parseInt(tpSel.value);
        const adminState = getAdminState();
        const tpl = adminState.templates ? adminState.templates.find(t => t.id === tId) : null;
        const tplCss = tpl ? tpl.css : `:root{--brand-green:#86BC25;--white:#FFF}*{box-sizing:border-box;margin:0;padding:0}.slide{width:100%;height:100%;background:var(--white);padding:40px;display:flex;flex-direction:column;flex-shrink:0}`;
        
        state.projectData.slides[tN] = { html: fH, css: tplCss, size: tpl?.size || "1411x794" }; 
        
        const idx = state.projectData.toc.findIndex(t => t.title === tN); 
        if(idx > -1) { state.projectData.toc[idx].status = "작성완료"; renderTOCEditor(); } 
        appState.saveCurrentProject(); window.updatePartSelectorOptions(); window.loadSlidePreview(0); 
    } catch(e) { alert("렌더링 실패: " + e.message); } finally { hideLoader(); } 
};

window.prepareSlidePrompt = async function() {
    const state = appState.getState();
    const tSel = document.getElementById('toc-selector'); 
    const tpSel = document.getElementById('template-selector'); 
    if(!tSel || !tpSel) return; 
    
    const tN = tSel.value, tId = String(tpSel.value);
    const adminState = getAdminState();
    let tpl = adminState.templates ? adminState.templates.find(t => String(t.id) === tId) : null;
    
    // Fallback if template is deleted or not found
    if(!tpl && adminState.templates && adminState.templates.length > 0) tpl = adminState.templates[0];
    if(!tpl) tpl = { size: "1411x794", html: "<div class='slide'><h1>Template Missing</h1></div>" };
    
    const tInfo = state.projectData.toc ? state.projectData.toc.find(t => t.title === tN) : null; 
    const pCnt = tInfo && tInfo.page ? parseInt(String(tInfo.page).replace(/[^0-9]/g, '')) || 1 : 1; 
    const sz = (tpl.size || "1411x794").split('x'); 
    const b = `<div class="slide" style="width:${sz[0]}px; height:${sz[1]}px; flex-shrink: 0; position: relative; overflow: hidden; background: var(--white);">`; 
    
    // Base Instruction from Admin Settings
    const basePrompt = adminState.prompts?.PROMPT_PROPOSAL_SLIDE || "";
    
    const safeStrategy = state.projectData.strategy ? String(state.projectData.strategy).substring(0,800) : "전략 없음";

    const p = `${basePrompt}

[1) 프로젝트 및 목차 정보]
- 프로젝트명: ${state.projectData.name || '제목 없음'}
- 현재 목차(Chapter): ${tN || '목차 없음'}
- 목표 분량: 총 **${pCnt}장**
- 상세 내용 가이드: ${tInfo?.detail || "내용을 충실히 작성할 것."}
- 전략 요약: ${safeStrategy}

[2) 템플릿 마스터 정보]
- HTML: ${tpl.html}
- CSS (반드시 이 스타일을 참고하여 클래스를 사용하라):
${tpl.css}

[3) 실행 지침]
- 반드시 위 **[템플릿 마스터 정보]**의 구조와 스타일(CSS)을 계승하여 HTML을 작성하라.
- 각 슬라이드는 \`<div class="slide">\` 태그로 구분하며, 총 **${pCnt}개**의 슬라이드를 연속해서 출력하라.
- 최상단 태그는 반드시 \`${b}\` 로 시작하고 \`</div>\`로 닫아야 함.
- 불필요한 설명 없이 HTML 코드 블록(\`\`\`html) 하나로만 응답하라.

[🔥 핵심 요청사항]: 요청된 분량은 총 **${pCnt}장**입니다. 논리적 흐름이 끊기지 않게 분배하여 반드시 \`<div class="slide">...</div>\` 형태의 슬라이드 블록을 정확히 **${pCnt}개 연속해서 출력**하세요.`;
    
    const a = document.getElementById('slide-prompt-textarea');
    const v = document.getElementById('prompt-view-mode'); 
    if(a) a.value = p; 
    if(v) v.innerHTML = marked.parse(p);
};

window.rewriteGonogo = async function() {
    const state = appState.getState();
    showLoader("의사결정안 재작성", "AI가 더 정교한 분석 결과를 생성하고 있습니다...");
    try {
        const p = `[현재 데이터]: ${state.projectData.rfpTexts}\n[현재 의사결정안]: ${state.projectData.gonogo}\n\n위 내용을 바탕으로 경영진이 신뢰할 수 있도록 리스크와 수익성을 더 객관적으로 분석하여 재작성하라.`;
        const res = await aiService.callAI(p, "", "PROMPT_PROPOSAL_GONOGO_REWRITE");
        state.projectData.gonogo = res;
        appState.saveCurrentProject();
        document.getElementById('gonogo-view-mode').innerHTML = marked.parse(res);
        document.getElementById('gonogo-input').value = res;
    } catch(e) { alert(e.message); } finally { hideLoader(); }
};

window.sendGonogoEmail = function() {
    const state = appState.getState();
    const subject = encodeURIComponent(`[보고] ${state.projectData.name} - 제안 참여 여부 검토 보고`);
    const body = encodeURIComponent(state.projectData.gonogo);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
};
function popoutDocumentPreview() {
    if (!activePreviewFile) {
        alert("미리보기 가능한 문서가 로드되지 않았습니다.");
        return;
    }
    
    const ext = activePreviewFile.name.split('.').pop().toLowerCase();
    const docName = activePreviewFile.name;

    if (activePreviewType === 'pdf' && activePreviewBlobUrl) {
        const win = window.open(activePreviewBlobUrl, "_blank");
        if (!win) alert("팝업 차단이 설정되어 있습니다. 팝업 차단을 해제해 주세요.");
        return;
    }

    const win = window.open("", "_blank", "width=900,height=950,scrollbars=yes,resizable=yes");
    if (!win) {
        alert("팝업 차단이 설정되어 있습니다. 팝업 차단을 해제해 주세요.");
        return;
    }

    let pagesHtml = '';

    if (activePreviewType === 'hwp') {
        for (let i = 0; i < hwpPageCount; i++) {
            try {
                const svgString = hwpDocInstance.renderPageSvg(i);
                if (svgString) {
                    pagesHtml += `
                    <div class="page-container shadow-lg border border-slate-200 rounded bg-white my-6 mx-auto relative p-4 flex items-center justify-center">
                        <div class="absolute top-2 left-2 text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">P. ${i + 1}</div>
                        <div class="svg-wrapper w-full h-auto flex justify-center">${svgString}</div>
                    </div>
                    `;
                }
            } catch (e) {
                pagesHtml += `
                <div class="page-container shadow-lg border border-rose-200 rounded bg-rose-50 my-6 mx-auto p-8 text-center text-rose-500 text-xs">
                    ${i + 1} 페이지 렌더링 실패: ${e.message}
                </div>
                `;
            }
        }
    } 
    else if (activePreviewType === 'text' || activePreviewType === 'office') {
        pagesHtml = `
        <div class="page-container shadow-lg border border-slate-200 rounded bg-white my-6 mx-auto p-12 text-slate-800">
            <div class="border-b border-slate-100 pb-4 mb-6 flex justify-between items-center text-xs text-slate-400 font-bold">
                <span>AURA DOC DOCUMENT RENDERER</span>
                <span>ORIGINAL TEXT PREVIEW</span>
            </div>
            <pre class="whitespace-pre-wrap text-xs text-slate-800 font-mono leading-relaxed">${activePreviewTextContent}</pre>
        </div>
        `;
    }

    win.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${docName} - AuraDoc 미리보기</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
                body {
                    background-color: #f1f5f9;
                    font-family: system-ui, -apple-system, sans-serif;
                }
                .page-container {
                    max-width: 820px;
                    min-height: 1050px;
                }
                svg {
                    width: 100% !important;
                    height: auto !important;
                    max-width: 100% !important;
                }
                @media print {
                    body {
                        background-color: #ffffff;
                        padding: 0;
                        margin: 0;
                    }
                    .page-container {
                        box-shadow: none;
                        border: none;
                        page-break-after: always;
                        margin: 0;
                        padding: 0;
                    }
                    .no-print {
                        display: none;
                    }
                }
            </style>
        </head>
        <body class="p-6">
            <div class="max-w-4xl mx-auto flex justify-between items-center mb-6 no-print bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <div>
                    <h1 class="text-base font-extrabold text-slate-800">📄 ${docName}</h1>
                    <p class="text-xs text-slate-400 mt-0.5">${activePreviewType === 'hwp' ? '총 ' + hwpPageCount + '페이지 • ' : ''}AuraDoc Intelligent Viewer</p>
                </div>
                <div class="flex gap-2">
                    <button onclick="window.print()" class="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-sm transition flex items-center gap-1.5">
                        🖨️ 인쇄 / PDF 저장
                    </button>
                    <button onclick="window.close()" class="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition">
                        닫기
                    </button>
                </div>
            </div>
            
            <div class="space-y-6">
                ${pagesHtml}
            </div>
        </body>
        </html>
    `);
    win.document.close();
}

export function initProposalUI() {
    if (typeof window !== 'undefined' && !window.__AURADOC_SOURCING_ATTACHMENT_EXTRACT_BOUND__) {
        window.__AURADOC_SOURCING_ATTACHMENT_EXTRACT_BOUND__ = true;
        window.addEventListener('auradoc:extractSourcingAttachments', (event) => {
            extractSourcingAttachments(event.detail || {});
        });
    }

    // HWP Preview controls
    document.getElementById('btn-hwp-popout')?.addEventListener('click', () => {
        popoutDocumentPreview();
    });

    document.getElementById('btn-hwp-prev')?.addEventListener('click', () => {
        if (hwpDocInstance && currentHwpPage > 0) {
            renderHwpPage(currentHwpPage - 1);
        }
    });

    document.getElementById('btn-hwp-next')?.addEventListener('click', () => {
        if (hwpDocInstance && currentHwpPage < hwpPageCount - 1) {
            renderHwpPage(currentHwpPage + 1);
        }
    });

    document.getElementById('btn-hwp-reset')?.addEventListener('click', () => {
        hwpDocInstance = null;
        currentHwpPage = 0;
        hwpPageCount = 0;
        
        const uploadZone = document.getElementById('p-upload-zone');
        const viewerContainer = document.getElementById('p-hwp-viewer-container');
        const fileInput = document.getElementById('p-file-upload');
        const viewerContent = document.getElementById('p-hwp-viewer-content');
        
        if (uploadZone && viewerContainer) {
            viewerContainer.classList.add('hidden');
            uploadZone.classList.remove('hidden');
        }
        if (fileInput) {
            fileInput.value = '';
        }
        if (viewerContent) {
            viewerContent.innerHTML = '<div class="text-slate-400 text-xs py-8 text-center">미리보기를 불러오는 중입니다...</div>';
        }
    });

    // Stepper bindings
    document.getElementById('btn-p-step-1')?.addEventListener('click', () => pStep(1));
    document.getElementById('btn-p-step-2')?.addEventListener('click', () => pStep(2));
    document.getElementById('btn-p-step-3')?.addEventListener('click', () => pStep(3));
    document.getElementById('btn-p-step-4')?.addEventListener('click', () => pStep(4));
    
    document.getElementById('btn-next-p2')?.addEventListener('click', runStep1);
    document.getElementById('btn-next-p3')?.addEventListener('click', runStep2);
    document.getElementById('btn-next-p4')?.addEventListener('click', () => {
        appState.setState({ proposalMaxStep: Math.max(appState.getState().proposalMaxStep, 4) });
        appState.saveCurrentProject(); 
        pStep(4);
    });

    document.getElementById('btn-gonogo-edit')?.addEventListener('click', toggleGonogoEdit);
    document.getElementById('btn-gonogo-rewrite')?.addEventListener('click', window.rewriteGonogo);
    document.getElementById('btn-gonogo-email')?.addEventListener('click', window.sendGonogoEmail);
    
    document.getElementById('btn-strategy-edit')?.addEventListener('click', () => {
        const v = document.getElementById('strategy-view-mode'), i = document.getElementById('strategy-input'), b = document.getElementById('btn-strategy-edit'); 
        if(!v || !i) return; 
        if(i.classList.contains('hidden')) { 
            i.value = appState.getState().projectData.strategy || ""; 
            v.classList.add('hidden'); i.classList.remove('hidden'); i.focus(); 
            if(b) b.innerHTML = "💾 저장"; 
        } else { 
            appState.getState().projectData.strategy = i.value; 
            if(v) {
                if (window.marked) v.innerHTML = marked.parse(appState.getState().projectData.strategy || "");
                else v.innerText = appState.getState().projectData.strategy || "";
            }
            v.classList.remove('hidden'); i.classList.add('hidden'); 
            if(b) b.innerHTML = "✏️ 편집"; 
            appState.saveCurrentProject();
        }
    });
    document.getElementById('btn-strategy-rewrite')?.addEventListener('click', window.rewriteStrategy);
    
    document.getElementById('btn-generate-slide')?.addEventListener('click', window.generateSlide);
    
    document.getElementById('toc-selector')?.addEventListener('change', () => {
        window.prepareSlidePrompt();
        loadSlidePreview();
    });
    document.getElementById('template-selector')?.addEventListener('change', () => {
        window.prepareSlidePrompt();
    });

    document.getElementById('btn-slide-prev')?.addEventListener('click', () => {
        window.loadSlidePreview((window.currentSlidePageIdx || 0) - 1);
    });
    document.getElementById('btn-slide-next')?.addEventListener('click', () => {
        window.loadSlidePreview((window.currentSlidePageIdx || 0) + 1);
    });

    document.getElementById('btn-prompt-edit')?.addEventListener('click', () => {
        const v = document.getElementById('prompt-view-mode');
        const i = document.getElementById('slide-prompt-textarea');
        const b = document.getElementById('btn-prompt-edit'); 
        if(!v || !i) return; 
        if(i.classList.contains('hidden')) { 
            i.value = i.value || v.innerText; 
            v.classList.add('hidden'); i.classList.remove('hidden'); i.focus(); 
            if(b) b.innerHTML = "💾 프롬프트 저장"; 
        } else { 
            if(v) v.innerHTML = marked.parse(i.value); 
            v.classList.remove('hidden'); i.classList.add('hidden'); 
            if(b) b.innerHTML = "✏️ 프롬프트 편집"; 
        } 
    });

    document.getElementById('btn-copy-slide-link')?.addEventListener('click', () => {
        const state = appState.getState();
        const tSel = document.getElementById('toc-selector');
        const tN = tSel?.value;
        if (!state.projectData.slides || !state.projectData.slides[tN]) {
            return alert("렌더링된 슬라이드가 없습니다. 먼저 생성해 주세요.");
        }
        const shareUrl = `${window.location.origin}${window.location.pathname}?project=${state.projectData.id}&part=${encodeURIComponent(tN)}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            alert('슬라이드 공유 링크가 클립보드에 복사되었습니다.\n(현재 로컬 환경에서는 URL만 생성됩니다)');
        });
    });
    
    document.getElementById('btn-print-pdf')?.addEventListener('click', () => {
        const state = appState.getState();
        const tSel = document.getElementById('toc-selector');
        const tN = tSel?.value;
        const sD = state.projectData.slides ? state.projectData.slides[tN] : null; 
        if (!sD) return alert("렌더링된 슬라이드가 없습니다.");
        
        const printWin = window.open('', '_blank');
        printWin.document.write(`<html><head><title>AuraDoc Print</title><style>@media print{@page{size: landscape; margin:0;}} body{margin:0;padding:0;} ${sD.css}</style></head><body>${sD.html}</body></html>`);
        printWin.document.close();
        setTimeout(() => {
            printWin.print();
            printWin.close();
        }, 500);
    });

    // Smart Upload Wiring
    document.getElementById('btn-upload-performance')?.addEventListener('click', () => document.getElementById('upload-performance')?.click());
    document.getElementById('upload-performance')?.addEventListener('change', (e) => handleSmartUpload(e, 'performance'));
    
    document.getElementById('btn-upload-org')?.addEventListener('click', () => document.getElementById('upload-org')?.click());
    document.getElementById('upload-org')?.addEventListener('change', (e) => handleSmartUpload(e, 'org'));

    document.getElementById('btn-upload-manpower')?.addEventListener('click', () => document.getElementById('upload-manpower')?.click());
    document.getElementById('upload-manpower')?.addEventListener('change', (e) => handleSmartUpload(e, 'manpower'));

    document.getElementById('btn-slide-ref')?.addEventListener('click', () => document.getElementById('slide-ref-upload')?.click());
    document.getElementById('slide-ref-upload')?.addEventListener('change', (e) => handleSmartUpload(e, 'ref'));

    window.handleSmartUpload = handleSmartUpload;
    window.showAiLogs = () => console.table(aiService.getLogs());
    
    document.getElementById('btn-full-slide-show')?.addEventListener('click', () => {
        const state = appState.getState();
        const tSel = document.getElementById('toc-selector');
        const tN = tSel?.value;
        const sD = state.projectData.slides ? state.projectData.slides[tN] : null; 
        if (!sD) return alert("렌더링된 슬라이드가 없습니다.");
        
        const slideParts = sD.html.split(/<div class="slide"/).filter(p => p.trim().length > 0);
        const slides = slideParts.map(p => `<div class="slide"` + p);
        
        const showWin = window.open('', '_blank');
        const slidesHtml = slides.join('');
        const winContent = `
            <html>
            <head>
                <style>
                    body{margin:0;padding:0;overflow:hidden;background:#000;color:#fff;font-family:sans-serif;}
                    ${sD.css}
                    .slides-container { display: flex; transition: transform 0.5s ease; }
                    .slide { flex: 0 0 100vw; height: 100vh; display: flex; justify-content: center; align-items: center; position: relative; }
                    .nav { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; gap: 20px; z-index: 1000; background: rgba(0,0,0,0.5); padding: 10px 20px; border-radius: 30px; }
                    .btn { background: none; border: 1px solid #fff; color: #fff; padding: 5px 15px; cursor: pointer; border-radius: 4px; }
                </style>
            </head>
            <body>
                <div class="slides-container" id="container">${slidesHtml}</div>
                <div class="nav">
                    <button class="btn" onclick="move(-1)">PREV</button>
                    <span id="info">1 / ${slides.length}</span>
                    <button class="btn" onclick="move(1)">NEXT</button>
                </div>
                <script>
                    let idx = 0;
                    const total = ${slides.length};
                    function move(d) {
                        idx = Math.max(0, Math.min(idx + d, total - 1));
                        document.getElementById('container').style.transform = 'translateX(' + (-idx * 100) + 'vw)';
                        document.getElementById('info').innerText = (idx + 1) + ' / ' + total;
                    }
                    window.addEventListener('keydown', e => {
                        if(e.key === 'ArrowRight') move(1);
                        if(e.key === 'ArrowLeft') move(-1);
                        if(e.key === 'Escape') window.close();
                    });
                </script>
            </body>
            </html>
        `;
        showWin.document.write(winContent);
        showWin.document.close();
    });

    document.getElementById('btn-show-precautions')?.addEventListener('click', () => {
        const state = appState.getState();
        const content = state.projectData.precautions || "먼저 Step 1 분석을 완료해주세요.";
        const modal = document.getElementById('precautions-modal');
        const view = document.getElementById('precautions-view-mode');
        const edit = document.getElementById('precautions-content');
        
        if (modal && view && edit) {
            view.innerHTML = marked.parse(content);
            edit.value = content;
            view.classList.remove('modal-hidden');
            edit.classList.add('modal-hidden');
            modal.classList.remove('modal-hidden');
        }
    });
    
    document.getElementById('btn-edit-precautions')?.addEventListener('click', () => {
        const view = document.getElementById('precautions-view-mode');
        const edit = document.getElementById('precautions-content');
        if (view && edit) {
            view.classList.add('modal-hidden');
            edit.classList.remove('modal-hidden');
            edit.focus();
        }
    });
    
    document.getElementById('btn-save-precautions')?.addEventListener('click', () => {
        const edit = document.getElementById('precautions-content');
        if (edit) {
            appState.getState().projectData.precautions = edit.value;
            appState.saveCurrentProject();
        }
        document.getElementById('precautions-modal')?.classList.add('modal-hidden');
    });

    document.getElementById('btn-close-precautions')?.addEventListener('click', () => {
        document.getElementById('precautions-modal')?.classList.add('modal-hidden');
    });

    // Use event delegation for dynamic buttons
    document.addEventListener('click', (e) => {
        const btnPopup = e.target.closest('#btn-popup-viewer');
        if (btnPopup) {
            const state = appState.getState();
            const tSel = document.getElementById('toc-selector');
            const tN = tSel?.value;
            const sD = state.projectData.slides ? state.projectData.slides[tN] : null; 
            
            if (sD && sD.html) {
                const win = window.open("", "_blank", "width=1200,height=800");
                if (win) {
                    const fullHtml = `<!DOCTYPE html><html><head><title>AuraDoc Preview</title><style>
                        body{margin:0;padding:0;background:#f0f2f5;display:flex;flex-direction:column;align-items:center;padding:40px 0;} 
                        ${sD.css}
                        .slide-wrap { background: white; box-shadow: 0 10px 25px rgba(0,0,0,0.2); margin-bottom: 40px; transform-origin: top center; overflow: hidden; }
                    </style></head><body>
                        <div style="margin-bottom: 20px; font-family: sans-serif; color: #64748b; font-size: 14px; font-weight: bold;">PREVIEW MODE - ${tN}</div>
                        ${sD.html}
                    </body></html>`;
                    win.document.write(fullHtml);
                    win.document.close();
                } else {
                    alert("팝업 차단을 해제해주세요.");
                }
            }
        }
    });

    // Title editing
    const titleInput = document.getElementById('project-title-input');
    titleInput?.addEventListener('change', (e) => {
        appState.getState().projectData.name = e.target.value;
        appState.saveCurrentProject();
    });

    // File Upload handling
    const fileUpload = document.getElementById('p-file-upload');
    if (fileUpload) {
        fileUpload.addEventListener('change', async (e) => {
            const files = e.target.files;
            if (files.length === 0) return;
            
            const statusLabel = document.getElementById('p-file-status');
            if (statusLabel) {
                statusLabel.innerText = "파일 추출 중...";
                statusLabel.classList.remove('hidden');
            }

            // 1. Text extraction
            const extractedTexts = await FileParser.parseFiles(Array.from(files));
            const textArea = document.getElementById('p-rfp-text');
            if (textArea) textArea.value = extractedTexts;
            
            if (statusLabel) {
                statusLabel.innerText = "추출 완료";
                setTimeout(() => statusLabel.classList.add('hidden'), 2000);
            }

            // 2. Hybrid Document Preview logic
            const supportedFile = Array.from(files).find(f => {
                const ext = f.name.split('.').pop().toLowerCase();
                return ['hwp', 'hwpx', 'pdf', 'docx', 'txt', 'md'].includes(ext);
            });

            if (supportedFile) {
                activePreviewFile = supportedFile;
                const ext = supportedFile.name.split('.').pop().toLowerCase();
                
                // Clean up previous blob URL to avoid memory leak
                if (activePreviewBlobUrl) {
                    URL.revokeObjectURL(activePreviewBlobUrl);
                    activePreviewBlobUrl = '';
                }

                // Show viewer container
                const uploadZone = document.getElementById('p-upload-zone');
                const viewerContainer = document.getElementById('p-hwp-viewer-container');
                const viewerContent = document.getElementById('p-hwp-viewer-content');
                const pageInfo = document.getElementById('hwp-page-info');
                const btnPrev = document.getElementById('btn-hwp-prev');
                const btnNext = document.getElementById('btn-hwp-next');
                
                if (uploadZone && viewerContainer) {
                    uploadZone.classList.add('hidden');
                    viewerContainer.classList.remove('hidden');
                }

                // Update container header title with selected document name
                const viewerTitle = viewerContainer?.querySelector('span');
                if (viewerTitle) {
                    viewerTitle.innerHTML = `<span class="text-indigo-600">📄</span> 원본 문서 미리보기 (${supportedFile.name})`;
                }

                if (ext === 'hwp' || ext === 'hwpx') {
                    activePreviewType = 'hwp';
                    // Show HWP-only pagination controls
                    if (pageInfo) pageInfo.classList.remove('hidden');
                    if (btnPrev) btnPrev.classList.remove('hidden');
                    if (btnNext) btnNext.classList.remove('hidden');
                    
                    try {
                        if (viewerContent) {
                            viewerContent.innerHTML = '<div class="text-slate-400 text-xs py-8 text-center">뷰어 모듈을 초기화하고 문서를 불러오고 있습니다...</div>';
                        }
                        await initRHWP();
                        const arrayBuffer = await supportedFile.arrayBuffer();
                        const uint8 = new Uint8Array(arrayBuffer);
                        const { HwpDocument } = await import('@rhwp/core');
                        hwpDocInstance = new HwpDocument(uint8);
                        hwpPageCount = hwpDocInstance.pageCount();
                        currentHwpPage = 0;
                        await renderHwpPage(0);
                    } catch (err) {
                        console.error("HWP Preview Error:", err);
                        if (viewerContent) {
                            viewerContent.innerHTML = `<div class="text-rose-500 text-xs py-8 text-center">미리보기를 불러오지 못했습니다: ${err.message}</div>`;
                        }
                    }
                } 
                else if (ext === 'pdf') {
                    activePreviewType = 'pdf';
                    // Hide HWP pagination controls
                    if (pageInfo) pageInfo.classList.add('hidden');
                    if (btnPrev) btnPrev.classList.add('hidden');
                    if (btnNext) btnNext.classList.add('hidden');
                    
                    activePreviewBlobUrl = URL.createObjectURL(supportedFile);
                    if (viewerContent) {
                        viewerContent.innerHTML = `
                            <iframe src="${activePreviewBlobUrl}" class="w-full h-full border-0 rounded-lg min-h-[450px]" style="background:#ffffff;"></iframe>
                        `;
                    }
                } 
                else if (ext === 'txt' || ext === 'md') {
                    activePreviewType = 'text';
                    if (pageInfo) pageInfo.classList.add('hidden');
                    if (btnPrev) btnPrev.classList.add('hidden');
                    if (btnNext) btnNext.classList.add('hidden');
                    
                    const text = await supportedFile.text();
                    activePreviewTextContent = text;
                    if (viewerContent) {
                        const truncatedText = text.length > 5000 ? text.slice(0, 5000) + "\n\n...(이하 생략 - 전체 본문은 우측 텍스트 영역 또는 새 창에서 보실 수 있습니다)..." : text;
                        viewerContent.innerHTML = `
                            <div class="w-full h-full overflow-auto bg-white p-6 rounded-lg border border-slate-200" style="min-h-[450px];">
                                <pre class="whitespace-pre-wrap text-xs text-slate-800 font-mono leading-relaxed">${truncatedText}</pre>
                            </div>
                        `;
                    }
                } 
                else if (['docx', 'xlsx', 'xls'].includes(ext)) {
                    activePreviewType = 'office';
                    if (pageInfo) pageInfo.classList.add('hidden');
                    if (btnPrev) btnPrev.classList.add('hidden');
                    if (btnNext) btnNext.classList.add('hidden');
                    
                    activePreviewTextContent = extractedTexts || "추출된 텍스트가 비어있습니다.";
                    if (viewerContent) {
                        const truncatedText = activePreviewTextContent.length > 3000 ? activePreviewTextContent.slice(0, 3000) + "\n\n...(이하 생략 - 전체 추출 본문은 우측 영역 또는 새 창에서 보실 수 있습니다)..." : activePreviewTextContent;
                        viewerContent.innerHTML = `
                            <div class="w-full h-full overflow-auto bg-white p-6 rounded-lg border border-slate-200" style="min-h-[450px];">
                                <div class="bg-indigo-50 border border-indigo-100 rounded-lg p-3.5 mb-4 text-xs text-indigo-700 leading-normal">
                                    <strong>💡 오피스 문서 추출 알림</strong><br>
                                    이 문서는 텍스트 추출 방식으로 제공됩니다. 전체 텍스트 대조 및 인쇄는 <strong>'새 창에서 보기'</strong> 버튼을 클릭하십시오.
                                </div>
                                <pre class="whitespace-pre-wrap text-xs text-slate-800 font-mono leading-relaxed">${truncatedText}</pre>
                            </div>
                        `;
                    }
                }
            }
        });
    }

    // TOC Excel Interop
    document.getElementById('btn-upload-toc-excel')?.addEventListener('click', () => {
        document.getElementById('p-toc-excel')?.click();
    });
    
    document.getElementById('p-toc-excel')?.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!confirm("엑셀 데이터를 업로드하면 기존에 작성된 모든 목차 데이터가 사라지고 엑셀 내용으로 덮어씌워집니다. 계속하시겠습니까?")) {
            e.target.value = '';
            return;
        }
        showLoader("엑셀 목차 업로드 중", "목차 데이터를 파싱하고 있습니다.");
        try {
            const data = await TemplateService.importTOCFromExcel(file);
            if (data && data.length > 0) {
                // Explicitly clear and replace to ensure reactivity and persistence
                const state = appState.getState();
                state.projectData.toc = [...data];
                appState.saveCurrentProject();
                
                // Full UI Refresh
                renderTOCEditor();
                window.updatePartSelectorOptions();
                
                alert(`총 ${data.length}개의 목차 항목을 성공적으로 불러왔습니다.`);
            } else {
                alert("엑셀 파일에서 목차를 찾을 수 없습니다. 양식을 확인해 주세요.");
            }
        } catch(err) {
            console.error("Excel Import Error:", err);
            alert("엑셀 업로드 실패: " + err.message);
        } finally {
            hideLoader();
            e.target.value = ''; 
        }
    });

    document.getElementById('btn-download-toc-excel')?.addEventListener('click', async () => {
        const state = appState.getState();
        if (!state.projectData.toc || state.projectData.toc.length === 0) {
            alert("다운로드할 목차 데이터가 없습니다.");
            return;
        }
        showLoader("엑셀 파일 생성 중", "목차 데이터를 엑셀로 변환하고 있습니다.");
        try {
            const blob = await TemplateService.exportTOCToExcel(state.projectData.toc, state.projectData.customCols);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `AuraDoc_TOC_${state.projectData.name || '제안서'}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch(err) {
            alert("다운로드 실패: " + err.message);
        } finally {
            hideLoader();
        }
    });

    // Step 2 & 3 bindings
    document.getElementById('btn-nogo-dropmail')?.addEventListener('click', () => {
        if(confirm("이 프로젝트를 Drop 하시겠습니까? 대시보드로 이동합니다.")) {
            const state = appState.getState();
            state.projectData.status = 'dropped';
            appState.saveCurrentProject();
            showDashboard();
        }
    });
    

    document.getElementById('btn-regenerate-toc')?.addEventListener('click', async () => {
        showLoader("목차 재도출", "구성 중..."); 
        try { 
            const state = appState.getState();
            const p = `[현재 전략]: ${state.projectData.strategy.substring(0,2000)}\n\n[요청]: 전략 바탕으로 제안 목차(TOC)만 다시 도출.\n(※ 양식 유지: | 레벨 | 목차명 | 분량(P) | 담당자 | 상태 | 세부 |)`; 
            const r = await aiService.callAI(p, "", "PROMPT_PROPOSAL_TOC_REGEN"); 
            parseMarkdownToTOC(r); 
            renderTOCEditor(); 
            appState.saveCurrentProject(); 
        } catch(e) { 
            alert("실패: " + e.message); 
        } finally { 
            hideLoader(); 
        }
    });

    // Add Row/Col listeners removed because they are handled by window. functions in index.html



    // Step 4 Bindings
    initStep4UploadUX();
    
    // Focus buttons Step 1
    const focusBtns = document.querySelectorAll('#p-step-1 button.text-sm.font-bold');
    focusBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            focusBtns.forEach(b => {
                b.classList.remove('bg-indigo-600', 'text-white');
                b.classList.add('text-slate-500', 'hover:text-indigo-600', 'hover:bg-indigo-50');
            });
            btn.classList.add('bg-indigo-600', 'text-white');
            btn.classList.remove('text-slate-500', 'hover:text-indigo-600', 'hover:bg-indigo-50');
        });
    });
}

function initStep4UploadUX() {
    // Buttons trigger file inputs
    document.getElementById('btn-upload-performance')?.addEventListener('click', () => document.getElementById('upload-performance')?.click());
    document.getElementById('btn-upload-org')?.addEventListener('click', () => document.getElementById('upload-org')?.click());
    document.getElementById('btn-upload-manpower')?.addEventListener('click', () => document.getElementById('upload-manpower')?.click());
    document.getElementById('btn-slide-ref')?.addEventListener('click', () => document.getElementById('slide-ref-upload')?.click());

    // File input listeners
    document.getElementById('upload-performance')?.addEventListener('change', (e) => handleSmartUpload(e, 'performance'));
    document.getElementById('upload-org')?.addEventListener('change', (e) => handleSmartUpload(e, 'org'));
    document.getElementById('upload-manpower')?.addEventListener('change', (e) => handleSmartUpload(e, 'manpower'));
    document.getElementById('slide-ref-upload')?.addEventListener('change', (e) => handleSmartUpload(e, 'ref'));

    // Modal UI listeners
    const modal = document.getElementById('smart-upload-modal');
    const tocSelect = document.getElementById('smart-upload-target-toc');
    const newTocGroup = document.getElementById('smart-upload-new-toc-group');

    document.getElementById('btn-close-smart-upload')?.addEventListener('click', () => modal?.classList.add('modal-hidden'));
    document.getElementById('btn-cancel-smart-upload')?.addEventListener('click', () => modal?.classList.add('modal-hidden'));

    tocSelect?.addEventListener('change', (e) => {
        if (e.target.value === 'new') {
            newTocGroup?.classList.remove('hidden');
        } else {
            newTocGroup?.classList.add('hidden');
        }
    });

    document.getElementById('btn-confirm-smart-upload')?.addEventListener('click', () => confirmSmartUpload());
}

let pendingSmartUpload = null;

async function handleSmartUpload(e, type) {
    const file = e.target.files[0];
    if (!file) return;
    
    showLoader("파일 파싱 중", "데이터를 추출하고 있습니다...");
    try {
        let previewText = "";
        let promptText = "";
        
        if (type === 'performance' || type === 'org' || type === 'manpower') {
            const texts = await FileParser.parseFiles([file]);
            previewText = texts;
            let prefix = "";
            let instruction = "";
            
            if (type === 'performance') {
                prefix = "[📊 사업 실적 데이터]";
                instruction = "사업명, 사업기간, 계약금액, 발주처, 수행 내용이 포함된 표 형태의 슬라이드를 작성하라.";
            } else if (type === 'org') {
                prefix = "[👥 프로젝트 조직도 데이터]";
                instruction = "이름, 직급, 역할이 명시된 조직도 형태의 슬라이드를 작성하라.";
            } else if (type === 'manpower') {
                prefix = "[📅 인력 투입 계획 데이터]";
                instruction = "단계별(분석, 설계, 개발, 테스트, 안정화 등) 투입 인력 및 M/M가 명시된 인력 투입 계획 표 슬라이드를 작성하라.";
            }
            
            promptText = `\n\n${prefix}\n아래 데이터를 바탕으로 ${instruction}\n${texts}`;
        } else if (type === 'ref') {
            previewText = "[이미지 파일 업로드 됨] - AI 비전 엔진을 통해 구조를 분석합니다.";
            promptText = `\n\n[🖼️ 사용자 업로드 유사 디자인 우선 적용]\n- 기존 템플릿을 무시하고 업로드된 참조 디자인의 레이아웃과 색상 톤을 최우선으로 사용하여 슬라이드를 렌더링하세요.`;
        }
        
        pendingSmartUpload = { type, file, promptText };
        
        // Populate modal TOC select
        const state = appState.getState();
        const tocSelect = document.getElementById('smart-upload-target-toc');
        if (tocSelect) {
            tocSelect.innerHTML = '<option value="new">🌟 새 목차 생성하여 적용</option>';
            state.projectData.toc?.forEach(t => {
                tocSelect.innerHTML += `<option value="${t.title}">[${t.level}] ${t.title}</option>`;
            });
            // Try to select current step 4 toc if available
            const currentToc = document.getElementById('toc-selector')?.value;
            if (currentToc) tocSelect.value = currentToc;
        }
        
        document.getElementById('smart-upload-new-toc-group')?.classList.add('hidden'); // default hidden if currentToc is selected
        if(tocSelect && tocSelect.value === 'new') document.getElementById('smart-upload-new-toc-group')?.classList.remove('hidden');
        
        const previewEl = document.getElementById('smart-upload-preview');
        if (previewEl) previewEl.innerText = previewText.substring(0, 500) + (previewText.length > 500 ? '...' : '');
        
        document.getElementById('smart-upload-modal')?.classList.remove('modal-hidden');
    } catch (err) {
        alert("파일 처리 실패: " + err.message);
    } finally {
        hideLoader();
        e.target.value = ''; // reset input
    }
}

function confirmSmartUpload() {
    if (!pendingSmartUpload) return;
    const state = appState.getState();
    const tocSelect = document.getElementById('smart-upload-target-toc');
    const newTocInput = document.getElementById('smart-upload-new-toc-title');
    
    let targetToc = tocSelect?.value;
    
    if (targetToc === 'new') {
        const newTitle = newTocInput?.value.trim() || '새 데이터 목차';
        state.projectData.toc = state.projectData.toc || [];
        state.projectData.toc.push({ level: "-", title: newTitle, page: "1", owner: "", status: "작성전", detail: "", custom: {} });
        targetToc = newTitle;
        renderTOCEditor();
        updatePartSelectorOptions();
        
        const mainTocSelector = document.getElementById('toc-selector');
        if (mainTocSelector) mainTocSelector.value = targetToc;
    }
    
    // Append to prompt
    const pA = document.getElementById('slide-prompt-textarea');
    const pV = document.getElementById('prompt-view-mode');
    
    // We update the textarea value for this specific TOC.
    // In AuraDoc, the prompt is per TOC, but right now it's a global textarea.
    // Let's just append it to the current textarea if the selector matches.
    const mainTocSelector = document.getElementById('toc-selector');
    if (mainTocSelector && mainTocSelector.value === targetToc) {
        if (pA) pA.value += pendingSmartUpload.promptText;
        if (pV) pV.innerHTML = marked.parse(pA.value);
    } else {
        // If they selected a different TOC, we would theoretically save it to that TOC's draft prompt.
        // For now, we switch the dropdown to that TOC, prepare prompt, and append.
        if (mainTocSelector) mainTocSelector.value = targetToc;
        window.prepareSlidePrompt(); // Prepare base prompt
        if (pA) pA.value += pendingSmartUpload.promptText;
        if (pV) pV.innerHTML = marked.parse(pA.value);
    }
    
    appState.saveCurrentProject();
    document.getElementById('smart-upload-modal')?.classList.add('modal-hidden');
    alert(`'${targetToc}' 목차에 데이터가 성공적으로 반영되었습니다.`);
    pendingSmartUpload = null;
}
